/**
 * B526 (e4) — OTA distribution, the tenant's management doors (CLASS-A RULED
 * 2026-10-01, Pathik, as recommended). A partner and what it sells are
 * CONFIGURATION (ruled 2026-09-29): the tenant ADMIN saves a partner (which
 * reseller channel its bookings land on, its hold length, its commercial
 * model) and the product mappings (our product / schedule / ticket type ↔
 * the partner's product / option / unit ids). The @transact/ota package
 * re-validates every save and names each refusal.
 *
 * The partner's credential is a key MINTED BY THE PLATFORM: only its digest
 * is stored, and the raw bearer is handed over ONCE through Secrets Manager
 * under the tenant's prefix — so neither the mint's response nor the key
 * list carries anything a caller could present. The strict responses below
 * have no field a bearer could ride.
 *
 * Every door is ADMIN on the tenant-config scope (channel.config.*'s shape):
 * a partner IS a channel's configuration.
 */
import { z } from "zod";
import { readOp, writeOp, zEpochMs, zId, zMoney } from "./common.js";
import { zOctoUnitType } from "./ota-shared.js";

/** Mirrors @transact/ota's OTA_DIALECTS (pinned equal by the handlers' test, through the save contracts). */
const zOtaDialect = z.enum(["OCTO", "VIATOR", "GETYOURGUIDE", "KLOOK", "EXPEDIA"]);

const zCommercial = z.discriminatedUnion("model", [
  z.object({ model: z.literal("COMMISSION"), rateBps: z.number().int().safe() }).strict(),
  z.object({ model: z.literal("PRICE_LIST"), priceListId: zId }).strict(),
]);

const zPartnerFields = {
  partnerId: zId,
  name: z.string().min(1).max(120),
  dialect: zOtaDialect,
  channelId: zId,
  status: z.enum(["ACTIVE", "PAUSED"]),
  holdTtlMs: z.number().int().safe().optional(),
  commercial: zCommercial,
};

const zPartner = z.object({ ...zPartnerFields, tenantId: zId }).strict();

const zUnitMapping = z
  .object({
    unitId: zId,
    variantId: zId,
    type: zOctoUnitType,
    internalName: z.string().min(1).max(120),
    minAge: z.number().int().safe().optional(),
    maxAge: z.number().int().safe().optional(),
  })
  .strict();

const zOptionMapping = z
  .object({
    optionId: zId,
    scheduleId: zId,
    internalName: z.string().min(1).max(120),
    default: z.boolean().optional(),
    cancellationCutoffHours: z.number().int().safe(),
    maxUnits: z.number().int().safe().optional(),
    units: z.array(zUnitMapping).min(1).max(50),
  })
  .strict();

const zMappingFields = {
  partnerId: zId,
  productId: zId,
  octoProductId: zId,
  internalName: z.string().min(1).max(120),
  options: z.array(zOptionMapping).min(1).max(50),
};

const zMapping = z.object({ ...zMappingFields, tenantId: zId }).strict();

/** A key as the tenant may see it — never its digest, never its secret. */
const zKeySummary = z
  .object({
    partnerId: zId,
    keyId: zId,
    issuedAt: zEpochMs,
    expiresAt: zEpochMs.optional(),
    revokedAt: zEpochMs.optional(),
    revokedReason: z.string().min(1).optional(),
    status: z.enum(["ACTIVE", "EXPIRED", "REVOKED"]),
  })
  .strict();

const EXAMPLE_PARTNER = {
  tenantId: "t-1",
  partnerId: "klook",
  name: "Klook",
  dialect: "OCTO" as const,
  channelId: "ch-klook",
  status: "ACTIVE" as const,
  commercial: { model: "COMMISSION" as const, rateBps: 2000 },
};

const EXAMPLE_MAPPING = {
  tenantId: "t-1",
  partnerId: "klook",
  productId: "p-ferry",
  octoProductId: "harbor-ferry",
  internalName: "Harbor ferry",
  options: [
    {
      optionId: "DEFAULT",
      scheduleId: "s-daily",
      internalName: "Daily crossing",
      cancellationCutoffHours: 24,
      units: [{ unitId: "adult", variantId: "v-adult", type: "ADULT" as const, internalName: "Adult" }],
    },
  ],
};

export const otaPartnerSave = writeOp({
  name: "ota.partner.save",
  permission: "channel:write",
  principals: ["STAFF:ADMIN"],
  fields: zPartnerFields,
  response: zPartner,
  exampleRequest: (({ tenantId: _t, ...rest }) => rest)(EXAMPLE_PARTNER),
  exampleResponse: EXAMPLE_PARTNER,
});

export const otaPartnerList = readOp({
  name: "ota.partner.list",
  permission: "channel:write",
  principals: ["STAFF:ADMIN"],
  fields: {},
  response: z.object({ partners: z.array(zPartner) }).strict(),
  exampleRequest: {},
  exampleResponse: { partners: [EXAMPLE_PARTNER] },
});

export const otaMappingSave = writeOp({
  name: "ota.mapping.save",
  permission: "channel:write",
  principals: ["STAFF:ADMIN"],
  fields: zMappingFields,
  response: zMapping,
  exampleRequest: (({ tenantId: _t, ...rest }) => rest)(EXAMPLE_MAPPING),
  exampleResponse: EXAMPLE_MAPPING,
});

export const otaMappingList = readOp({
  name: "ota.mapping.list",
  permission: "channel:write",
  principals: ["STAFF:ADMIN"],
  fields: { partnerId: zId },
  response: z.object({ mappings: z.array(zMapping) }).strict(),
  exampleRequest: { partnerId: "klook" },
  exampleResponse: { mappings: [EXAMPLE_MAPPING] },
});

/**
 * Mint a partner key. The bearer goes to Secrets Manager under the tenant's
 * prefix (`handover` names the secret) and NOWHERE else — the response is
 * strict and carries no secret.
 */
export const otaKeyMint = writeOp({
  name: "ota.key.mint",
  permission: "channel:write",
  principals: ["STAFF:ADMIN"],
  gate: "admin",
  fields: { partnerId: zId, expiresAt: zEpochMs.optional() },
  response: z.object({ key: zKeySummary, handover: z.string().min(1) }).strict(),
  exampleRequest: { partnerId: "klook" },
  exampleResponse: {
    key: { partnerId: "klook", keyId: "k8f3Hq2LmNp4RsT6", issuedAt: 1_790_000_000_000, status: "ACTIVE" as const },
    handover: "transact/dev/tenants/t-1/ota/klook/k8f3Hq2LmNp4RsT6",
  },
});

export const otaKeyRevoke = writeOp({
  name: "ota.key.revoke",
  permission: "channel:write",
  principals: ["STAFF:ADMIN"],
  fields: { partnerId: zId, keyId: zId, reason: z.string().min(1).max(200).optional() },
  response: z.object({ key: zKeySummary }).strict(),
  exampleRequest: { partnerId: "klook", keyId: "k8f3Hq2LmNp4RsT6", reason: "rotated" },
  exampleResponse: {
    key: { partnerId: "klook", keyId: "k8f3Hq2LmNp4RsT6", issuedAt: 1_790_000_000_000, revokedAt: 1_790_000_100_000, revokedReason: "rotated", status: "REVOKED" as const },
  },
});

export const otaKeyList = readOp({
  name: "ota.key.list",
  permission: "channel:write",
  principals: ["STAFF:ADMIN"],
  fields: { partnerId: zId },
  response: z.object({ keys: z.array(zKeySummary) }).strict(),
  exampleRequest: { partnerId: "klook" },
  exampleResponse: { keys: [{ partnerId: "klook", keyId: "k8f3Hq2LmNp4RsT6", issuedAt: 1_790_000_000_000, status: "ACTIVE" as const }] },
});

/**
 * B526 (f3) — the Live Operations partner card's read: every partner the
 * tenant configured, its last inbound call as the OCTO wire answered it
 * (absent = it has never called), and what it sold since `fromMs` (the
 * caller's local day start). `today.total` is absent when nothing with a
 * stamped total sold — never a zero in a guessed currency.
 */
export const otaPartnerActivity = readOp({
  name: "ota.partner.activity",
  permission: "channel:write",
  principals: ["STAFF:ADMIN"],
  fields: { fromMs: zEpochMs },
  response: z
    .object({
      partners: z.array(
        z
          .object({
            partnerId: zId,
            name: z.string().min(1),
            status: z.enum(["ACTIVE", "PAUSED"]),
            channelId: zId,
            health: z
              .object({
                lastCallAt: zEpochMs,
                lastOutcome: z.enum(["OK", "REFUSED", "FAILED"]),
                lastOkAt: zEpochMs.optional(),
                lastFailureAt: zEpochMs.optional(),
                lastFailureStatus: z.number().int().optional(),
              })
              .strict()
              .optional(),
            today: z.object({ bookings: z.number().int().nonnegative(), total: zMoney.optional() }).strict(),
          })
          .strict(),
      ),
    })
    .strict(),
  exampleRequest: { fromMs: 1_790_000_000_000 },
  exampleResponse: {
    partners: [
      {
        partnerId: "klook",
        name: "Klook",
        status: "ACTIVE" as const,
        channelId: "ch-klook",
        health: { lastCallAt: 1_790_000_060_000, lastOutcome: "OK" as const, lastOkAt: 1_790_000_060_000 },
        today: { bookings: 3, total: { amount: 12_600, currency: "USD" } },
      },
    ],
  },
});
